import React, { useCallback, useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { LogoutOutlined, EyeOutlined } from '@ant-design/icons';
import { getCurrentImpersonation, finishImpersonation } from '../utils/impersonation';
import { exitImpersonation } from '../modules/auth/api';
import * as partnerApi from '../modules/partners/api';

/**
 * Top-of-page banner shown whenever the current JWT carries an
 * `impersonatedBy` claim. "Exit impersonation" restores the admin token from
 * sessionStorage and navigates back to the partner detail page where the
 * impersonation started.
 *
 * Mounts in MainLayout AND PartnerLayout so it's visible regardless of which
 * layout the impersonated session renders.
 */
export const IMPERSONATION_BANNER_HEIGHT = 32;

export default function ImpersonationBanner() {
  const [info, setInfo] = useState(() => getCurrentImpersonation());
  const [partnerName, setPartnerName] = useState(null);
  const [exiting, setExiting] = useState(false);

  // Re-read on cookie changes (best-effort — js-cookie has no event API, so
  // we poll on a slow interval; this only matters for the brief moment the
  // banner needs to appear / disappear).
  useEffect(() => {
    const tick = () => setInfo(getCurrentImpersonation());
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Push the rest of the page down by the banner height so the fixed header
  // and the impersonation strip don't overlap. We do this via a CSS variable
  // so layouts (Main, PartnerLayout) can read it in their paddingTop.
  useEffect(() => {
    const px = info ? `${IMPERSONATION_BANNER_HEIGHT}px` : '0px';
    document.documentElement.style.setProperty('--impersonation-banner-height', px);
    return () => {
      document.documentElement.style.setProperty('--impersonation-banner-height', '0px');
    };
  }, [info]);

  // Resolve the partner name once we know we're impersonating.
  useEffect(() => {
    if (!info?.partnerId) {
      setPartnerName(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { response } = await partnerApi.getMyPartner();
        if (!cancelled && response?.name) setPartnerName(response.name);
      } catch {
        // Endpoint might not exist on older deployments — fall back to id.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [info?.partnerId]);

  const handleExit = useCallback(async () => {
    if (exiting) return;
    setExiting(true);
    try {
      // Tell the backend we're exiting — adds an IMPERSONATE_END audit entry.
      // Fire-and-forget: even if this fails, we still want to restore locally.
      try {
        await exitImpersonation();
      } catch {
        // ignore
      }
      const { restored, returnPath } = finishImpersonation();
      if (!restored) {
        message.warning('Could not restore admin session — please log in again');
        window.location.assign('/login');
        return;
      }
      // Hard reload so Redux re-bootstraps with the restored admin cookie.
      // (Soft state-rehydration would need a coordinated reset of every slice
      // that cached partner-viewer data; reload is cheap and bulletproof.)
      window.location.assign(returnPath || '/admin/partners');
    } finally {
      setExiting(false);
    }
  }, [exiting]);

  if (!info) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10002,
        background: '#fa8c16',
        color: '#fff',
        padding: '6px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 13,
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <EyeOutlined />
        <span>
          <strong>Impersonating viewer of {partnerName || `partner ${info.partnerId}`}</strong>
          {info.impersonatedByEmail ? (
            <span style={{ marginLeft: 8, opacity: 0.85 }}>as {info.impersonatedByEmail}</span>
          ) : null}
        </span>
      </div>
      <Button
        size="small"
        icon={<LogoutOutlined />}
        loading={exiting}
        onClick={handleExit}
        style={{ background: 'rgba(255,255,255,0.95)', borderColor: 'transparent', color: '#fa8c16', fontWeight: 600 }}
      >
        Exit impersonation
      </Button>
    </div>
  );
}
